
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Introduction to Debt Management',
    content: [
      '<strong>Learning Goal:</strong> Learn how to manage debt responsibly and avoid common pitfalls in the Philippines.',
      'Debt can be useful if managed well, but it can also lead to financial stress. Understanding how to control debt is key to financial wellness.'
    ]
  },
  {
    title: 'Types of Debt in the Philippines',
    content: [
      'Common debts include personal loans, credit cards, and informal loans ("5-6"). Know the terms and interest rates before borrowing.'
    ]
  },
  {
    title: 'Strategies for Managing Debt',
    content: [
      'Pay on time, prioritize high-interest debts, and avoid loan traps. Use BSP resources for guidance: <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/DebtManagement.aspx" target="_blank">Debt Management</a>.'
    ]
  },
  {
    title: 'Seeking Help and Support',
    content: [
      'If overwhelmed, seek help from financial counselors or government agencies. Avoid loan sharks and report scams to authorities.'
    ]
  }
];

const FinancialPlanningModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep } = useModuleProgress('financialPlanningM3');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;
  const isLastStep = safeCurrentStep === totalSteps - 1;

  const handleNext = () => {
    if (safeCurrentStep < totalSteps - 1) {
      setCurrentStep(safeCurrentStep + 1);
    }
  };
  const handleBack = () => {
    if (safeCurrentStep === 0) {
      navigate('/panel/financial-planning');
      return;
    }
    setCurrentStep(safeCurrentStep - 1);
  };

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Debt Management</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn how to manage and reduce debt, understand good vs. bad debt, and access support resources for Filipinos.
            </p>
          </div>
        </div>
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>
            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph, index) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph, index) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {isLastStep ? (
              <button className="btn-primary" onClick={() => navigate('/panel/financial-planning')}>Finish Module</button>
            ) : (
              <button className="btn-primary" onClick={handleNext}>Next</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPlanningModule3Page;

